import React from "react";
import { Box, Typography, styled } from "@mui/material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import StorefrontIcon from "@mui/icons-material/Storefront";
import { Order } from "../../types/types";
import { DELIVERY_PICKUP } from "../../utils/deliveryQuote";
import { formatDateSpanish } from "../../utils/format";
import { DELIVERY_TEXTS, ORDER_TEXTS } from "../shared/constants";

const Block = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  marginTop: theme.spacing(2),
}));

const Heading = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

const Guarantee = styled(Typography)(({ theme }) => ({
  display: "inline-block",
  marginTop: theme.spacing(1),
  padding: theme.spacing(0.25, 1),
  borderRadius: theme.spacing(0.75),
  fontWeight: 700,
  color: theme.palette.warning.dark,
  backgroundColor: theme.palette.action.hover,
}));

/**
 * What the shopper was promised, read off the order rather than recomputed.
 *
 * Every number here was decided by `POST /orders` and stored on the row. Re-deriving any of it in
 * the browser would mean the confirmation page and the shop could disagree the moment a fee is
 * edited or Sunday's sync changes a product's stock — which is exactly what snapshotting these
 * fields onto the order was for (README §6.1).
 *
 * Renders nothing for orders placed before EB-548, which carry none of these fields.
 */
const OrderDelivery: React.FC<{ order: Order }> = ({ order }) => {
  const isPickup = order.delivery_method === DELIVERY_PICKUP;
  const hasDelivery = isPickup || Boolean(order.delivery_municipality);
  if (!hasDelivery) return null;

  const hasPromise =
    order.promise_days_min !== null &&
    order.promise_days_min !== undefined &&
    order.promise_days_max !== null &&
    order.promise_days_max !== undefined;

  return (
    <Block>
      <Heading>
        {isPickup ? <StorefrontIcon fontSize="small" /> : <LocalShippingIcon fontSize="small" />}
        <Typography variant="subtitle1" fontWeight="bold">
          {ORDER_TEXTS.deliveryTitle}
        </Typography>
      </Heading>

      <Typography variant="body2">
        {isPickup
          ? ORDER_TEXTS.pickupAt(order.pickup_address ?? "")
          : ORDER_TEXTS.deliveryTo(order.delivery_municipality ?? "")}
      </Typography>

      {!isPickup && order.address && (
        <Typography variant="body2" color="text.secondary">
          {ORDER_TEXTS.addressLabel} {order.address}
        </Typography>
      )}
      {!isPickup && order.delivery_instructions && (
        <Typography variant="body2" color="text.secondary">
          {ORDER_TEXTS.instructionsLabel} {order.delivery_instructions}
        </Typography>
      )}

      {hasPromise && (
        <Typography variant="body2" mt={1}>
          {ORDER_TEXTS.promiseLabel}{" "}
          {DELIVERY_TEXTS.promise(
            order.promise_days_min as number,
            order.promise_days_max as number,
          )}
        </Typography>
      )}

      {/* The guarantee as a date, so the advisor and the shopper read the same one off the same
          record instead of each counting days from a different day. */}
      {order.price_hold_until && (
        <Guarantee variant="caption">
          {ORDER_TEXTS.priceHold(formatDateSpanish(order.price_hold_until))}
        </Guarantee>
      )}
    </Block>
  );
};

export default OrderDelivery;
